// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSessionStore from '@/app/store/session';
import useCustomPayStore from '@/app/store/customPay';
import actionsOrganizations from '../../api/actionsOrganizations';
// Components
import ConnectionPointsPanel from '@/app/modules/organizations/components/details/ConnectionPointsPanel';
import LegalInfosPanel from '@/app/modules/organizations/components/details/LegalInfosPanel';
import OrganizationDetailsPanel from '@/app/modules/organizations/components/details/OrganizationDetailsPanel';
import CustomizationPanel from '@/app/modules/organizations/components/details/CustomizationPanel';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/common-utils/components/ui/accordion';
import SimOffersPanel from '@/app/modules/organizations/components/details/SimOffersPanel';
import AlertsPanel from '@/app/modules/organizations/components/details/AlertsPanel';
// Common
import ICONS from '@/common-utils/enums/icons';
import organizationFields from '../../fields/organizationFields';
import logoSocieteDotCom from 'url:../../../../../../resource/images/logoSocieteDotCom.png';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import ORGANIZATION_TYPES from '../../enums/OrganizationTypes';
import DETAILS_TAB_SECTIONS from '../../enums/sections/DETAILS_TAB_SECTIONS';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.organization
 * @param root0.section
 * @returns {JSX} OrganizationDetailsTab
 */
function OrganizationDetailsTab({ organization, section }) {

    // Store
    const { uuid: loggedUserOrganizationId } = useRootOrganizationStore(s => s.rootOrganization);
    const { permissions: userPermissions } = useSessionStore(s => s.session);
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);

    // State
    const [launchRefetch, setLaunchRefetch] = useState(true);
    const [openDetails, setOpenDetails] = useState(false);
    const [openConnectionPoints, setOpenConnectionPoints] = useState(false);
    const [openLegalInfos, setOpenLegalInfos] = useState(false);
    const [openCustomization, setOpenCustomization] = useState(false);
    const [openAlerts, setOpenAlerts] = useState(false);
    const [openSimOffers, setOpenSimOffers] = useState(false);

    // Api calls
    const { refetch } = actionsOrganizations.getOrganization(organization?.uuid, true);

    // Hooks
    useEffect(() => {
        if (organization && launchRefetch) {
            refetch();
            setLaunchRefetch(false);
        }
    }, [launchRefetch, organization, refetch]);

    // Constants
    const moreInfoUrl = organization ? `https://www.societe.com/cgi-bin/search?champs=${organization[organizationFields.REGISTRATION_NUMBER.key].substr(0, 9)}` : null;

    // Functions
    const hasAlertMailReadPermissions = () => {
        return hasRequiredPermissions({
            permissions: userPermissions,
            requiredPermissions: [Permissions.ORGANIZATION_GET_ALERT_POS.name, Permissions.ORGANIZATION_GET_ALERT_SIMS.name]
        });
    };
    const isOfType = (...organizationTypes) => {
        return organization && organizationTypes.some(organizationType => organization[organizationFields.TYPE.key] === organizationType.name);
    };
    const getDefaultAccordionValue = () => {
        if (section === DETAILS_TAB_SECTIONS.ALERTS) {
            return [DETAILS_TAB_SECTIONS.ALERTS];
        }
        else {
            return [DETAILS_TAB_SECTIONS.DETAILS];
        }
    };

    return (
        <Accordion type='multiple' defaultValue={getDefaultAccordionValue()}>
            <AccordionItem className='rounded-md' value={DETAILS_TAB_SECTIONS.DETAILS} >
                <AccordionTrigger
                    className='opacity-[.8]'
                    open={openDetails}
                    onClick={() => setOpenDetails(!openDetails)}
                >
                    <div className='flex items-center gap-2'>
                        <Icon icon={ICONS.ORGANIZATION} />
                        <Entity entity='page.organization.accordions.details' />
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    <OrganizationDetailsPanel organization={organization} setLaunchRefetch={setLaunchRefetch} />
                </AccordionContent>
            </AccordionItem>
            {
                organization.connectionPoints.length > 0 &&
                <AccordionItem className='rounded-md' value={DETAILS_TAB_SECTIONS.CONNECTION_POINTS}>
                    <AccordionTrigger
                        className='opacity-[.8]'
                        open={openConnectionPoints}
                        onClick={() => setOpenConnectionPoints(!openConnectionPoints)}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.CONNECTION} />
                            <Entity entity={'page.organization.accordions.connection_points'} />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <ConnectionPointsPanel connectionPoints={organization.connectionPoints} organization={organization} />
                    </AccordionContent>
                </AccordionItem>
            }
            <AccordionItem className='rounded-md' value={DETAILS_TAB_SECTIONS.LEGAL_INFOS}>
                <AccordionTrigger
                    className='opacity-[.8]'
                    open={openLegalInfos}
                    onClick={() => setOpenLegalInfos(!openLegalInfos)}
                >
                    <div className='flex items-center gap-2'>
                        <Icon icon={ICONS.INFO} />
                        <Entity entity='page.organization.accordions.legal_infos' />
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    {
                        organization.legalInfos ? <LegalInfosPanel legalInfos={organization.legalInfos} showTitle={false} />
                            : <>
                                <Entity entity='keys.no_legal_info' namespace={nsOrganization} />
                                <div className='flex gap-2'>
                                    <Entity entity='keys.more_legal_info' namespace={nsOrganization} />
                                    <a href={moreInfoUrl} target='_blank'>
                                        <img alt='' className='with-text' src={logoSocieteDotCom} />
                                    </a>
                                </div>
                            </>
                    }

                </AccordionContent>
            </AccordionItem>
            {
                (loggedUserOrganizationId === organization.uuid && hasAlertMailReadPermissions()) &&
                <AccordionItem className='rounded-md' value={DETAILS_TAB_SECTIONS.ALERTS}>
                    <AccordionTrigger
                        className='opacity-[.8]'
                        open={openAlerts}
                        onClick={() => setOpenAlerts(!openAlerts)}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.ALERTS} />
                            <Entity entity='page.organization.accordions.alerts' />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <AlertsPanel />
                    </AccordionContent>
                </AccordionItem>
            }
            {(!isCustomPay && isOfType(ORGANIZATION_TYPES.WHITE_LABEL)) &&
                <AccordionItem className='rounded-md' value={DETAILS_TAB_SECTIONS.SIM_OFFERS}>
                    <AccordionTrigger
                        className='opacity-[.8]'
                        open={openSimOffers}
                        onClick={() => setOpenSimOffers(!openSimOffers)}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.GPRS} />
                            <Entity entity='page.organization.accordions.sim_offers' />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <SimOffersPanel organizationId={organization.uuid} />
                    </AccordionContent>
                </AccordionItem>
            }
            {
                organization.brand &&
                <AccordionItem className='rounded-md' value={DETAILS_TAB_SECTIONS.CUSTOMIZATION}>
                    <AccordionTrigger
                        className='opacity-[.8]'
                        open={openCustomization}
                        onClick={() => setOpenCustomization(!openCustomization)}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.CUSTOMIZATION} />
                            <Entity entity='page.organization.accordions.white_label_customization' />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <CustomizationPanel
                            brand={organization.brand}
                            organization={organization}
                            setLaunchRefetch={setLaunchRefetch}
                        />
                    </AccordionContent>
                </AccordionItem>
            }
        </Accordion>
    );
}

OrganizationDetailsTab.propTypes = {
    organization: PropTypes.object.isRequired,
    section: PropTypes.string
};

export default OrganizationDetailsTab;